/**
 * Staff Family Portal tab service (Phase 12, plan §3A "Staff routes").
 * Replaces the retired POST/GET/DELETE /patients/{id}/guardians routes.
 *
 * SECURITY: never log guardian names or emails; only IDs and error codes.
 */

import { apiClient } from '../api-client';
import { isApiError } from '../../dtos/common/api.dto';
import type {
  FamilyPortalState,
  FamilyPortalInvite,
  FamilyPortalUser,
  ApprovePortalUserRequest,
  RevokePortalUserRequest,
  Age18ReviewRequest,
  CannotInviteReason,
} from '../../dtos/portal/familyPortalAdmin.dto';

const base = (patientId: string) => `/patients/${encodeURIComponent(patientId)}/portal`;

/** Why the Invite button is disabled (shown next to it). */
export const CANNOT_INVITE_TEXT: Record<CannotInviteReason, string> = {
  NO_EMAIL: 'Add an email address for this guardian before inviting.',
  PATIENT_INACTIVE: 'Invites are not available for discharged patients.',
  PATIENT_DECEASED: 'Invites are not available for this patient.',
  NOT_ENTITLED: "Your plan doesn't include the family portal.",
  INVITE_PENDING: 'An invite is already pending. Resend or cancel it first.',
  ALREADY_LINKED: 'This guardian already has portal access.',
};

const ACTION_ERRORS: Record<string, string> = {
  ...CANNOT_INVITE_TEXT,
  ENTITLEMENT_REQUIRED: "Your plan doesn't include the family portal.",
  FORBIDDEN: "You don't have permission to do this.",
};

/** A safe message for a failed Family Portal action. */
export function familyPortalErrorMessage(err: unknown, fallback = 'The action failed. Please try again.'): string {
  if (!isApiError(err)) return fallback;
  if (err.domainCode && ACTION_ERRORS[err.domainCode]) return ACTION_ERRORS[err.domainCode];
  if (err.statusCode === 403) return ACTION_ERRORS.FORBIDDEN;
  return fallback;
}

export class FamilyPortalService {
  static async getState(patientId: string): Promise<FamilyPortalState> {
    return (await apiClient.get<FamilyPortalState>(base(patientId))).data;
  }

  static async invite(patientId: string, patientGuardianId: string): Promise<FamilyPortalInvite> {
    const res = await apiClient.post<{ invite: FamilyPortalInvite }>(`${base(patientId)}/invites`, { patientGuardianId });
    return res.data.invite;
  }

  static async resendInvite(patientId: string, inviteId: string): Promise<FamilyPortalInvite> {
    const res = await apiClient.post<{ invite: FamilyPortalInvite }>(
      `${base(patientId)}/invites/${encodeURIComponent(inviteId)}/resend`
    );
    return res.data.invite;
  }

  static async cancelInvite(patientId: string, inviteId: string): Promise<FamilyPortalInvite> {
    const res = await apiClient.post<{ invite: FamilyPortalInvite }>(
      `${base(patientId)}/invites/${encodeURIComponent(inviteId)}/cancel`
    );
    return res.data.invite;
  }

  static async approve(patientId: string, familyPortalUserId: string, body: ApprovePortalUserRequest): Promise<FamilyPortalUser> {
    const res = await apiClient.post<{ portalUser: FamilyPortalUser }>(
      `${base(patientId)}/users/${encodeURIComponent(familyPortalUserId)}/approve`,
      body
    );
    return res.data.portalUser;
  }

  static async revoke(patientId: string, familyPortalUserId: string, body: RevokePortalUserRequest): Promise<FamilyPortalUser> {
    const res = await apiClient.post<{ portalUser: FamilyPortalUser }>(
      `${base(patientId)}/users/${encodeURIComponent(familyPortalUserId)}/revoke`,
      body
    );
    return res.data.portalUser;
  }

  static async age18Review(patientId: string, familyPortalUserId: string, body: Age18ReviewRequest): Promise<FamilyPortalUser> {
    const res = await apiClient.post<{ portalUser: FamilyPortalUser }>(
      `${base(patientId)}/users/${encodeURIComponent(familyPortalUserId)}/age18-review`,
      body
    );
    return res.data.portalUser;
  }
}

export default FamilyPortalService;
